/**
 * 结算场景：总结算 + 名字输入（A-Z0-9 选择式）+ 提交排行榜 + 显示排名。
 * checksum 与 server/leaderboard.js 的 checksumOf 一致：
 *   sha256(`${name}|${score}|${character}|${stageReached}|${cleared}|${difficulty}|kimi-shmup-v1`) 前 16 hex。
 */
import { VIEW_W, VIEW_H } from '../config.js';
import { Input } from '../engine/input.js';
import { cycleIndex } from '../engine/menu.js';
import { CHARACTERS } from '../content/characters.js';
import { getDifficulty } from '../content/difficulties.js';
import { t, tf, getLang, diffName } from '../i18n.js';

const CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
const COLS = 9;
const MAX_NAME = 12;
const CHECKSUM_SALT = 'kimi-shmup-v1';
// 字符格之后的两个功能键
const CELL_DEL = CHARSET.length;     // 'DEL'
const CELL_END = CHARSET.length + 1; // 'END'
const CELL_COUNT = CHARSET.length + 2;

/** WebCrypto 计算 checksum（node 18+ 亦有 globalThis.crypto，冒烟环境安全）。 */
async function checksumOf(name, score, character, stageReached, cleared, difficulty) {
  const raw = `${name}|${score}|${character}|${stageReached}|${cleared}|${difficulty}|${CHECKSUM_SALT}`;
  const buf = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('').slice(0, 16);
}

/** 角色显示名：英文 romaji，中文 cnName(romaji)。 */
function charLabel(id) {
  const c = CHARACTERS.find((x) => x.id === id);
  if (!c) return String(id);
  return getLang() === 'zh' ? `${c.cnName}(${c.name})` : c.name;
}

export const ResultScene = {
  mgr: null,

  enter(params) {
    this.info = params; // {score, graze, pointV, maxPointV, captured, attempted, continues, cleared, stageReached, character, difficulty, replayPayload}
    this.name = '';
    this.cell = 0;
    this.phase = 'input'; // 'input' | 'submitting' | 'done'
    this.rank = null;
    this.error = null;
    this.t = 0;
    this.mgr.shared.audio.playBgm('bgm_title');
    // 录像本地保存（最近 10 条，回放列表用）
    if (this.info.replayPayload) {
      try {
        const KEY = 'kimi-replays';
        const list = JSON.parse(globalThis.localStorage?.getItem(KEY) || '[]');
        list.unshift({ id: `local-${Date.now()}`, date: new Date().toISOString(), ...this.info.replayPayload });
        globalThis.localStorage?.setItem(KEY, JSON.stringify(list.slice(0, 10)));
      } catch (e) { /* 存储失败忽略 */ }
    }
    // HISCORE 本地记录（仅供 HUD 显示）
    try {
      const best = Number(globalThis.localStorage?.getItem('kimi-hiscore') || 0);
      if (this.info.score > best) {
        globalThis.localStorage?.setItem('kimi-hiscore', String(this.info.score));
        if (this.mgr.shared.state) this.mgr.shared.state.hiscore = this.info.score;
      }
    } catch (e) { /* 无痕模式等：忽略 */ }
  },

  exit() {},

  update(dt) {
    this.t += dt;
    const audio = this.mgr.shared.audio;
    if (this.phase === 'done') {
      if (Input.pressed('confirm')) {
        audio.playSfx('select');
        this.mgr.switchTo('title');
      }
      return;
    }
    if (this.phase !== 'input') return;

    let moved = false;
    if (Input.pressed('left')) { this.cell = cycleIndex(this.cell, CELL_COUNT, -1); moved = true; }
    if (Input.pressed('right')) { this.cell = cycleIndex(this.cell, CELL_COUNT, 1); moved = true; }
    if (Input.pressed('up')) { this.cell = cycleIndex(this.cell, CELL_COUNT, -COLS); moved = true; }
    if (Input.pressed('down')) { this.cell = cycleIndex(this.cell, CELL_COUNT, COLS); moved = true; }
    if (moved) audio.playSfx('cursor');

    if (Input.pressed('cancel')) {
      this.name = this.name.slice(0, -1);
      audio.playSfx('cursor');
    }
    if (Input.pressed('confirm')) {
      if (this.cell === CELL_DEL) {
        this.name = this.name.slice(0, -1);
        audio.playSfx('cursor');
      } else if (this.cell === CELL_END) {
        if (this.name.length > 0) {
          audio.playSfx('select');
          this._submit();
        }
      } else if (this.name.length < MAX_NAME) {
        this.name += CHARSET[this.cell];
        audio.playSfx('cursor');
      }
    }
  },

  async _submit() {
    this.phase = 'submitting';
    const i = this.info;
    const difficulty = i.difficulty || 'normal';
    // fetch 超时兜底：服务端挂起时不会永远停在"提交中"（历史 bug）
    const fetchTO = (url, opts = {}, ms = 15000) => {
      const ctl = new AbortController();
      const timer = setTimeout(() => ctl.abort(), ms);
      return fetch(url, { ...opts, signal: ctl.signal }).finally(() => clearTimeout(timer));
    };
    try {
      // 先上传录像（服务端强制要求 replayId，录像失败则成绩必然提交不了——不装"不阻塞"）
      let replayId = null;
      if (i.replayPayload) {
        const rres = await fetchTO('/api/replays', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(i.replayPayload),
        });
        const rdata = await rres.json();
        if (!rres.ok) throw new Error(`录像上传失败: ${rdata.error || rres.status}`);
        replayId = rdata.id;
      }
      const checksum = await checksumOf(this.name, i.score, i.character, i.stageReached, i.cleared, difficulty);
      const res = await fetchTO('/api/scores', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: this.name,
          score: i.score,
          character: i.character,
          stageReached: i.stageReached,
          cleared: i.cleared,
          difficulty,
          graze: i.graze,
          maxMultiplier: +(i.maxPointV / 10000).toFixed(2),
          replayId,
          checksum,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      this.rank = data.rank;
    } catch (e) {
      this.error = e && e.name === 'AbortError' ? '提交超时，请检查网络后重试' : String((e && e.message) || e);
    }
    this.phase = 'done';
  },

  draw(r) {
    const i = this.info;
    r.rect(0, 0, VIEW_W, VIEW_H, { fill: '#001', alpha: 0.6 });
    r.text(i.cleared ? t('result.allClear') : t('result.gameover'), VIEW_W / 2, 50, { size: 26, color: i.cleared ? '#fc3' : '#f66', align: 'center' });

    // 总结算
    const rows = [
      [t('result.score'), String(i.score)],
      [t('result.graze'), String(i.graze)],
      [t('result.maxPoint'), String(i.maxPointV)],
      [t('result.spell'), `${i.captured}/${i.attempted}`],
      [t('result.continues'), String(i.continues)],
      [t('result.stage'), `${i.stageReached}${i.cleared ? t('result.cleared') : ''}`],
      [t('result.character'), charLabel(i.character)],
      [t('result.difficulty'), diffName(getDifficulty(i.difficulty))],
    ];
    for (let k = 0; k < rows.length; k++) {
      const y = 100 + k * 24;
      r.text(rows[k][0], 110, y, { size: 13, color: '#8cf' });
      r.text(rows[k][1], VIEW_W - 110, y, { size: 13, color: '#fff', align: 'right' });
    }

    if (this.phase === 'input') {
      r.text(t('result.nameHint'), VIEW_W / 2, 286, { size: 11, color: '#889', align: 'center' });
      // 已输入名字
      r.rect(140, 306, 200, 26, { fill: '#000', stroke: '#46f' });
      r.text(this.name + (Math.floor(this.t * 2) % 2 === 0 ? '_' : ''), VIEW_W / 2, 312, { size: 16, color: '#ff4', align: 'center' });
      // 字符网格
      const gw = 36;
      const gx = (VIEW_W - COLS * gw) / 2;
      const gy = 344;
      for (let k = 0; k < CELL_COUNT; k++) {
        const x = gx + (k % COLS) * gw;
        const y = gy + Math.floor(k / COLS) * 30;
        const sel = k === this.cell;
        if (sel) r.rect(x - 2, y - 2, 28, 24, { fill: '#224', stroke: '#ff4' });
        const label = k === CELL_DEL ? 'DEL' : k === CELL_END ? 'END' : CHARSET[k];
        r.text(label, x + 12, y + 4, { size: 12, color: sel ? '#ff4' : '#ccc', align: 'center' });
      }
    } else if (this.phase === 'submitting') {
      r.text(t('result.submitting'), VIEW_W / 2, 320, { size: 14, color: '#8cf', align: 'center' });
    } else {
      if (this.rank !== null) {
        r.text(tf('result.rankOk', { n: this.rank }), VIEW_W / 2, 330, { size: 16, color: '#fc3', align: 'center' });
      } else {
        r.text(`${t('result.fail')}${this.error}`, VIEW_W / 2, 330, { size: 12, color: '#f88', align: 'center' });
      }
      r.text(t('result.back'), VIEW_W / 2, 370, { size: 12, color: '#889', align: 'center' });
    }
  },
};
